import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSwimming } from '../context/SwimmingContext';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ResultsPDF from '../components/ResultsPDF';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import logoSwimteamPro from '../assets/logo_swimteampro.png';

function Results() {
  const { formatTime, totalTime, poolLength, raceDistance } = useSwimming();
  const [selectedDistance, setSelectedDistance] = useState('all');
  const [sortBy, setSortBy] = useState('distance');
  const [localResults, setLocalResults] = useState({});

  useEffect(() => {
    const loadResults = () => {
      const storedResults = localStorage.getItem('results');
      if (storedResults) {
        try {
          const parsedResults = JSON.parse(storedResults);
          setLocalResults(parsedResults);
        } catch (error) {
          console.error('Erro ao carregar resultados:', error);
        }
      }
    };

    loadResults();
    window.addEventListener('focus', loadResults);
    window.addEventListener('storage', loadResults);
    
    return () => {
      window.removeEventListener('focus', loadResults);
      window.removeEventListener('storage', loadResults);
    };
  }, []);

  const maxDistance = Math.max(...Object.values(localResults || {})
    .map(data => (data.laps?.length || 0) * (raceDistance || poolLength)));

  const availableDistances = Array.from(
    { length: maxDistance / (raceDistance || poolLength) },
    (_, i) => (i + 1) * (raceDistance || poolLength)
  );

  const calcularEstatisticasGerais = () => {
    const tempoTotalEquipe = Object.values(localResults || {}).reduce((total, result) => {
      const tempoVoltas = result.laps?.reduce((sum, lap) => {
        const lapTime = typeof lap === 'object' ? lap.time : lap;
        return sum + lapTime;
      }, 0) || 0;
      return total + tempoVoltas;
    }, 0);

    const metragemTotal = Object.values(localResults || {}).reduce((total, result) => {
      return total + ((result.laps?.length || 0) * (raceDistance || poolLength));
    }, 0);

    return {
      tempoTotalEquipe,
      metragemTotal
    };
  };

  const processLapTime = (lap) => {
    if (typeof lap === 'object') {
      return lap.time || lap.lapTime || lap;
    }
    return lap;
  };

  const getFileName = () => {
    const institutionName = localStorage.getItem('institutionName');
    const eventName = localStorage.getItem('eventName');
    const currentYear = new Date().getFullYear();

    if (institutionName && eventName) {
      const abreviacao = institutionName
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase();
      
      return `${abreviacao}_${eventName.replace(/\s+/g, '_')}_${currentYear}.pdf`;
    }
    
    return `SwimTeamPro_Resultados_${currentYear}.pdf`;
  };

  const sortedResults = Object.entries(localResults || {})
    .map(([swimmer, data]) => {
      const processedLaps = data.laps?.map(processLapTime) || [];
      const totalSwimTime = processedLaps.reduce((acc, lap) => acc + lap, 0);
      const distance = processedLaps.length * (raceDistance || poolLength);
      const totalLaps = Math.ceil(distance / poolLength);
      const averageTime = processedLaps.length > 0 ? totalSwimTime / processedLaps.length : 0;
      const speedMS = distance / (totalSwimTime / 1000);
      const speedKMH = speedMS * 3.6;

      return {
        swimmer,
        laps: processedLaps,
        average: averageTime,
        lastUpdate: data.lastUpdate,
        distance,
        totalLaps,
        totalSwimTime,
        speedMS,
        speedKMH,
        performance: processedLaps.length > 0
          ? (distance / (totalSwimTime / 1000))
          : 0
      };
    })
    .filter(result => {
      if (selectedDistance === 'all') return true;
      return result.distance >= parseInt(selectedDistance);
    })
    .sort((a, b) => {
      if (selectedDistance === 'all') {
        const distanceA = a.distance;
        const distanceB = b.distance;
        if (distanceA !== distanceB) {
          return distanceB - distanceA;
        }
      }
      
      switch(sortBy) {
        case 'bestTime':
          return Math.min(...(a.laps || [Infinity])) - Math.min(...(b.laps || [Infinity]));
        case 'worstTime':
          return Math.max(...(b.laps || [0])) - Math.max(...(a.laps || [0]));
        case 'average':
          return (a.average || 0) - (b.average || 0);
        default:
          return (a.average || 0) - (b.average || 0);
      }
    });

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 4,
          mt: 2
        }}
      >
        <Box
          component="img"
          src={logoSwimteamPro}
          alt="Logo SwimTeam Pro"
          sx={{
            width: 'auto',
            height: { xs: '60px', sm: '80px', md: '100px' },
            objectFit: 'contain'
          }}
        />
      </Box>

      <Box sx={{ my: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" sx={{ 
          mb: { xs: 2, sm: 4 }, 
          textAlign: 'center',
          fontSize: { xs: '1.75rem', sm: '2.125rem' }
        }}>
          Ranking Final da Competição
        </Typography>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Box sx={{ 
              p: 3, 
              bgcolor: 'background.paper', 
              borderRadius: 2,
              boxShadow: 3
            }}>
              <Typography variant="h6" gutterBottom color="primary">
                Resumo da Competição
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle1" color="text.secondary">
                    Tamanho da Piscina
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {poolLength}m
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle1" color="text.secondary">
                    Total de Voltas
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {Math.ceil(calcularEstatisticasGerais().metragemTotal / poolLength)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle1" color="text.secondary">
                    Distância da Prova
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {raceDistance || poolLength}m
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle1" color="text.secondary">
                    Metragem Total
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {calcularEstatisticasGerais().metragemTotal}m
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle1" color="text.secondary">
                    Tempo Total de Prova
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {formatTime(totalTime || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle1" color="text.secondary">
                    Tempo Total de Competição
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {formatTime(calcularEstatisticasGerais().tempoTotalEquipe || 0)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Filtrar por Distância Mínima</InputLabel>
              <Select
                value={selectedDistance}
                label="Filtrar por Distância Mínima"
                onChange={(e) => setSelectedDistance(e.target.value)}
                sx={{ bgcolor: 'background.paper' }}
              >
                <MenuItem value="all">Todas as Distâncias</MenuItem>
                {availableDistances.map(distance => (
                  <MenuItem key={distance} value={distance}>
                    Até {distance}m
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Ordenar por</InputLabel>
              <Select
                value={sortBy}
                label="Ordenar por"
                onChange={(e) => setSortBy(e.target.value)}
                sx={{ bgcolor: 'background.paper' }}
              >
                <MenuItem value="bestTime">Melhor Tempo</MenuItem>
                <MenuItem value="worstTime">Pior Tempo</MenuItem>
                <MenuItem value="average">Média de Tempo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {sortedResults.map((result, index) => (
            <Grid item xs={12} key={result.swimmer}>
              <Accordion 
                defaultExpanded={index === 0}
                sx={{
                  bgcolor: index === 0 ? 'primary.light' : 
                          index === 1 ? 'secondary.light' :
                          index === 2 ? 'warning.light' : 
                          'background.paper',
                  color: index <= 2 ? 'white' : 'inherit',
                  '& .MuiAccordionSummary-root': {
                    bgcolor: index === 0 ? 'primary.light' : 
                            index === 1 ? 'secondary.light' :
                            index === 2 ? 'warning.light' : 
                            'background.paper',
                    color: index <= 2 ? 'white' : 'inherit'
                  }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ color: index <= 2 ? 'white' : 'inherit' }} />}
                >
                  <Box sx={{ 
                    width: '100%',
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 2, sm: 0 }
                  }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        {index === 0 ? '🥇 ' : index === 1 ? '🥈 ' : index === 2 ? '🥉 ' : ''}{index + 1}º Lugar: {result.swimmer}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 2 },
                        mt: { xs: 1, sm: 0 }
                      }}>
                        <Typography>
                          Voltas na Piscina: {result.totalLaps}
                        </Typography>
                        <Typography>
                          Distância: {result.distance}m
                        </Typography>
                        <Typography>
                          Tempo Médio: {formatTime(result.average)}
                        </Typography>
                        <Typography>
                          Tempo Total: {formatTime(result.totalSwimTime)}
                        </Typography>
                        <Typography>
                          Veloc.(m/s): {result.speedMS.toFixed(2)}
                        </Typography>
                        <Typography>
                          Veloc.(km/h): {result.speedKMH.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Detalhes das Voltas:
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Registro</TableCell>
                          <TableCell>Distância</TableCell>
                          <TableCell>Tempo</TableCell>
                          <TableCell>Diferença</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.laps?.map((lap, i) => {
                          const lapTime = typeof lap === 'object' ? lap.time : lap;
                          const prevLap = i > 0 ? result.laps[i - 1] : null;
                          const prevTime = prevLap ? (typeof prevLap === 'object' ? prevLap.time : prevLap) : 0;
                          const difference = lapTime - prevTime;
                          
                          return (
                            <TableRow key={i}>
                              <TableCell>{i + 1}</TableCell>
                              <TableCell>{raceDistance || poolLength}m</TableCell>
                              <TableCell>{formatTime(lapTime)}</TableCell>
                              <TableCell sx={{ 
                                color: i === 0 ? 'text.primary' : 
                                       difference > 0 ? 'error.main' : 
                                       'success.main',
                                fontWeight: 'bold'
                              }}>
                                {i === 0 ? '-' : (
                                  difference > 0 
                                    ? `+${formatTime(difference)}` 
                                    : formatTime(difference)
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>

        {sortedResults.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: 4,
            mb: 2 
          }}>
            <PDFDownloadLink
              document={
                <ResultsPDF 
                  results={sortedResults}
                  formatTime={formatTime}
                  totalTime={totalTime}
                  poolLength={poolLength}
                  raceDistance={raceDistance}
                />
              }
              fileName={getFileName()}
            >
              {({ loading }) => (
                <Button
                  variant="contained"
                  color="success"
                  disabled={loading}
                  startIcon={<PictureAsPdfIcon />}
                  sx={{ 
                    minWidth: 250,
                    py: 2,
                    fontSize: '1.1rem',
                    boxShadow: 3,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    }
                  }}
                >
                  {loading ? 'Gerando PDF...' : 'Exportar Resultados Oficiais'}
                </Button>
              )}
            </PDFDownloadLink>
          </Box>
        )}
      </Box>
    </Container>
  );
}

export default Results;